import { bool, func, number, object, string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import VisibilitySensor from 'react-visibility-sensor';

import { isEmpty } from 'ravenjs/utils/lodash';

import { DEFAULT_COLORS } from 'constants/colors';
import AppSubNavigationMain from 'components/AppSubNavigationMain';
import AppSubNavigationTopics from 'components/AppSubNavigationTopics';
import { hasFeatureFlag } from 'utils/common';
import { HOVER_BUFFER_ZONE, SUBMENU_TOP_OFFSET } from 'constants/navigation';

const AppSubNavigationStyledCommon = styled.div`
    display: flex;
    position: absolute;
    background: ${DEFAULT_COLORS.WHITE};
    color: ${DEFAULT_COLORS.BLACK};
    z-index: 100;
    right: ${({ subNavOverflow }) => (subNavOverflow ? 0 : 'auto')};
    margin: 0 0.625rem 0 0;
`;

const AppSubNavigationStyledV1 = styled(AppSubNavigationStyledCommon)`
    top: 42px;
    border: 1px solid ${DEFAULT_COLORS.BLACK};
    padding: 2.094rem 2.094rem 2.094rem 2.094rem;
`;

const AppSubNavigationStyledV2 = styled(AppSubNavigationStyledCommon)`
    top: 52px;
    border: 1px solid ${DEFAULT_COLORS.WHITE_4};
    border-radius: 4px 4px 8px 8px;
    box-shadow: 0 2px 8px -2px rgba(40, 40, 40, 0.16);
    padding: 24px;
    width: fit-content;

    &::before {
        content: '';
        position: absolute;
        top: -${HOVER_BUFFER_ZONE + SUBMENU_TOP_OFFSET}px;
        left: 0;
        right: 0;
        height: ${HOVER_BUFFER_ZONE + SUBMENU_TOP_OFFSET}px;
    }

    /* Extended hover zone for cursor forgiveness (pointer interaction tolerance) */
    &::after {
        content: '';
        position: absolute;
        top: -${HOVER_BUFFER_ZONE}px;
        left: -${HOVER_BUFFER_ZONE}px;
        right: -${HOVER_BUFFER_ZONE}px;
        bottom: -${HOVER_BUFFER_ZONE}px;
        z-index: -1;
    }
`;

const ArrowStyled = styled.div`
    width: 16px;
    height: 16px;
    border-top: 1px solid ${DEFAULT_COLORS.BLACK};
    border-left: 1px solid ${DEFAULT_COLORS.BLACK};
    position: absolute;
    top: 34px;
    right: 40%;
    transform: rotate(45deg);
    background: ${DEFAULT_COLORS.WHITE};
    z-index: 1000;
    box-shadow: none;
`;

const AppSubNavigation = props => {
    const {
        isImpersonatingUser,
        show,
        nav,
        scrollTop,
        subnav,
        state,
        isVisibleOnDOM,
        history,
        onMouseEnter,
        onMouseLeave,
    } = props;

    const [subNavOverflow, setSubNavOverflow] = useState(false);
    const [overflowChecked, setOverflowChecked] = useState(false);

    const isNavigationV2Enabled = history?.location
        ? hasFeatureFlag(history.location, 'showNavigationV2')
        : false;

    useEffect(() => {
        if (isNavigationV2Enabled) {
            setOverflowChecked(false);
            setSubNavOverflow(false);
        }
    }, [state, show, isNavigationV2Enabled]);

    const hasSubNav = () => {
        return (subnav.main && subnav.main.length) || (subnav.topics && subnav.topics.length);
    };

    const onClick = e => {
        // Need to stop propagation of event as this component is child of <NavItem>
        e.stopPropagation();
    };

    // Handler for mouse enter on submenu (V2 only - cancels close timeout)
    const handleMouseEnter = () => {
        if (isNavigationV2Enabled && onMouseEnter) {
            onMouseEnter();
        }
    };

    // Handler for mouse leave from submenu (V2 only - triggers delayed close)
    const handleMouseLeave = () => {
        if (isNavigationV2Enabled && onMouseLeave) {
            onMouseLeave();
        }
    };

    const renderTopicsOrContentSection = () => {
        if (!isEmpty(subnav.topics)) {
            if (isNavigationV2Enabled) {
                return (
                    <AppSubNavigationTopics
                        nav={nav}
                        items={subnav.topics}
                        isNavigationV2Enabled={isNavigationV2Enabled}
                    />
                );
            }

            return (
                <div style={{ marginLeft: '4rem' }}>
                    <AppSubNavigationTopics nav={nav} items={subnav.topics} />
                </div>
            );
        }
        return null;
    };

    const Container = isNavigationV2Enabled ? AppSubNavigationStyledV2 : AppSubNavigationStyledV1;

    if (show && isVisibleOnDOM && hasSubNav()) {
        return (
            <>
                {!isNavigationV2Enabled && <ArrowStyled />}
                <VisibilitySensor
                    onChange={isVisible => {
                        if (!overflowChecked) {
                            setSubNavOverflow(!isVisible);
                        }
                        setOverflowChecked(true);
                    }}
                >
                    <Container
                        isImpersonatingUser={isImpersonatingUser}
                        subNavOverflow={subNavOverflow}
                        scrollTop={scrollTop}
                        // V2 hover handlers for persistent hover behavior
                        onMouseEnter={isNavigationV2Enabled ? handleMouseEnter : undefined}
                        onMouseLeave={isNavigationV2Enabled ? handleMouseLeave : undefined}
                    >
                        <div
                            onClick={onClick}
                            onKeyDown={onClick}
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: isNavigationV2Enabled ? '24px' : undefined,
                                alignItems: isNavigationV2Enabled ? 'flex-start' : undefined,
                            }}
                        >
                            <AppSubNavigationMain
                                items={subnav.main}
                                mainState={state}
                                isNavigationV2Enabled={isNavigationV2Enabled}
                            />
                            {renderTopicsOrContentSection()}
                        </div>
                    </Container>
                </VisibilitySensor>
            </>
        );
    }

    return null;
};

AppSubNavigation.propTypes = {
    isImpersonatingUser: bool,
    scrollTop: number,
    show: bool,
    state: string,
    subnav: object,
    nav: object,
    isVisibleOnDOM: bool,
    history: object,
    onMouseEnter: func,
    onMouseLeave: func,
};

AppSubNavigation.defaultProps = {
    isImpersonatingUser: false,
    scrollTop: 0,
    show: false,
    state: '',
    subnav: {
        main: [],
        topics: [],
    },
    nav: {},
    isVisibleOnDOM: true,
    history: {},
    onMouseEnter: undefined,
    onMouseLeave: undefined,
};

export { AppSubNavigation as AppSubNavigationUnwrapped };
export default withRouter(AppSubNavigation);
